import React, { useContext, useState } from 'react';
import { Typography, Button } from '@one-thd/sui-atomic-components';
import { StarFilled } from '@one-thd/sui-icons';
import {
  ExperienceContext,
  useConfigService,
} from '@thd-nucleus/experience-context';
import VirtualCheckoutID from '@thd-olt-component-react/virtual-checkout-id';
import { List } from '../../common/List/List';
import {
  getVerifiedBenefitsList,
  VERIFIED_ADMIN,
  VERIFIED_USER,
} from '../../../constants/constants';
import { MaxDiscountMessage } from '../../common/MaxDiscountMessage/MaxDiscountMessage';
import { isB2BUser } from '../../../utils/utils';
import { useVeteransDayChallenge } from '../../../hooks/useVeteransDayChallenge';
import { track } from '../../../analytics/analytics';
import { VIRTUAL_ID_BTN } from '../../../constants/analytics';
import { getCheckoutAccessTypeLabel } from '../../../constants/general';
import type { ExperienceContextValue } from '../../../types/experience-context';

interface VerifiedUserProps {
  /* Straight off `militaryDiscountDetails`, so null as well as absent. */
  isMilitaryDiscountCapped?: boolean | null;
  /* The member's registration date, checked against the Veterans Day Challenge cutoff. */
  verificationDate?: string | null;
  /* The verified user type resolved by ProgramSection. */
  userType?: string;
}

function VerifiedUser({
  isMilitaryDiscountCapped = false,
  verificationDate,
  userType,
}: VerifiedUserProps) {
  const { channel } = useContext<ExperienceContextValue>(ExperienceContext);
  const [showQrCode, setShowQrCode] = useState(false);
  const isMobile = channel === 'mobile';
  const isTablet = channel === 'tablet';
  const showVerifyBtn = !isMilitaryDiscountCapped && (isMobile || isTablet);
  const isB2B = isB2BUser();
  const enableHdPass = !!useConfigService('ENABLE_HD_PASS_VERBIAGE');
  const checkoutAccessType = getCheckoutAccessTypeLabel(enableHdPass);
  const isVeteransDayChallengeActive =
    useVeteransDayChallenge(verificationDate);
  /* Only B2C members and B2B admins get the challenge - never purchasers. */
  const showVeteransDayChallengeCta =
    (userType === VERIFIED_USER || userType === VERIFIED_ADMIN) &&
    isVeteransDayChallengeActive;

  return (
    <div className="sui-grid sui-gap-y-6 sui-mt-4 sui-w-full">
      {isMilitaryDiscountCapped && <MaxDiscountMessage />}
      <div>
        <div className="sui-flex sui-gap-x-2">
          <StarFilled color="brand" size="large" />
          <Typography variant="h2" color="brand">
            You&apos;re Enrolled!
          </Typography>
        </div>
        <Typography>Use your discount to shop online or in store.</Typography>
      </div>
      <List list={getVerifiedBenefitsList(checkoutAccessType)} />
      {/* Placeholder until the loyalty-benefits sweepstakes CTA is ready. */}
      {showVeteransDayChallengeCta && (
        <Button data-testid="veteransDayChallengeCta" variant="secondary">
          Veterans Day Challenge
        </Button>
      )}
      {showVerifyBtn && (
        <Button
          data-testid="showQRCodeBtn"
          onClick={() => {
            track(VIRTUAL_ID_BTN);
            setShowQrCode(true);
          }}
        >
          View {checkoutAccessType}
        </Button>
      )}

      {showQrCode && (
        <div data-testid="qrCode">
          <VirtualCheckoutID
            isB2B={!!isB2B}
            enableHdPass={enableHdPass}
            onClose={() => {
              setShowQrCode(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default VerifiedUser;
